import "./Notifications.css";
import { useState, useEffect, Fragment } from "react";
import Notification from "./Notification";
import { Header, Content, Main } from "../commons/Template/Template";
import { FormLoader } from "../commons/Form/Form";
import { useTranslation, Trans } from "react-i18next";
import { Input } from "../commons/Form/Form";
import { useAuth } from "../../auth/AuthContext";
import api from "../../api/resources";
import { CheckBoxOutlined, Close } from "@mui/icons-material";
import Button from "../commons/Button/Button";
import { useToastsDispatch } from "../commons/Toasts/ToastsContext";
import externalRoutes from "../../routes/externalRoutes";
import notificationURLs from "./notificationURLs";

export default function Notifications() {
  const { t } = useTranslation(["notifications"]);
  document.title = t("notifications:title");

  return (
    <Main themeSwitch>
      <Header
        heading={t("notifications:heading")}
        subheading={t("notifications:subheading")}
      />
      <Content>
        <NotificationsComponent />
      </Content>
    </Main>
  );
}

export function NotificationsComponent({ companyID = "" }) {
  const { t } = useTranslation(["common", "notifications", "financing"]);
  const { onNotificationRead } = useAuth();
  const dispatchToast = useToastsDispatch();
  const [notifications, setNotifications] = useState([]);
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [selectAllToggle, setSelectAllToggle] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectAllShown, setSelectAllShown] = useState(true);
  const [menuShown, setMenuShown] = useState(false);

  const sortNotifications = (notifications) => {
    const unreadNotifications = notifications.filter(
      (notification) => !notification.read,
    );
    const readNotifications = notifications.filter(
      (notification) => notification.read,
    );
    const sortByDescendingDate = (a, b) => (a.timestamp < b.timestamp ? 1 : -1);
    unreadNotifications.sort(sortByDescendingDate);
    if (unreadNotifications.length === 0) setSelectAllShown(false);
    readNotifications.sort(sortByDescendingDate);

    return [...unreadNotifications, ...readNotifications];
  };

  const init = async () => {
    const postProcess = (rawNotifications) => {
      const convertDataIntoProps = (rawData) => {
        const { key, ...rest } = rawData;
        const { reason } = rest;

        let messageValues = rest;

        const commonProps = {
          title: t(`notifications:keys.${key}.title`),
          message: (
            <Trans
              shouldUnescape
              t={t}
              i18nKey={`notifications:keys.${key}.message`}
              values={messageValues}
              count={+!!reason} // `count` is ignored if `i18n` key does not have plurals set
            />
          ),
        };

        const urls = notificationURLs;
        const url = urls[key];

        const allProps = {
          ...commonProps,
          ...(url && {
            // button is added to props only when url is specified
            button: {
              text: t(`notifications:keys.${key}.button`),
              url:
                key === "SEPA_verification_failed"
                  ? externalRoutes.Stripe.root
                  : url,
              isExternalURL: key === "SEPA_verification_failed",
            },
          }),
        };

        return companyID ? { ...allProps, button: null } : allProps;
      };

      const sortedNotifications = sortNotifications(rawNotifications);

      let finalNotifications = [];
      for (const notif of sortedNotifications) {
        const newNotif = {
          id: notif.id,
          timestamp: notif.timestamp,
          read: notif.read,
          onAdd: (id) => setSelectedIDs((prevIDs) => [...prevIDs, id]),
          onRemove: (id) =>
            setSelectedIDs((prevIDs) =>
              prevIDs.filter((prevID) => prevID !== id),
            ),
          ...convertDataIntoProps(notif.data),
        };

        finalNotifications = [...finalNotifications, newNotif];
      }

      return finalNotifications;
    };

    let rawNotifications = [];
    const response = await api.getNotifications(companyID || "");
    if (response.ok) {
      rawNotifications = await response.json();
    }

    setNotifications(postProcess(rawNotifications));
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const handleSelectAllToggleChange = () => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((prevNotification) => ({
        ...prevNotification,
        selectedByControl: selectAllToggle,
      })),
    );
  };

  useEffect(() => {
    handleSelectAllToggleChange();
  }, [selectAllToggle]);

  const handleSelectAllToggleUpdate = () => {
    if (selectedIDs.length === 0) {
      setSelectAllToggle(false);
    }

    if (selectedIDs.length === notifications.length) {
      setSelectAllToggle(true);
    }
  };

  useEffect(() => {
    handleSelectAllToggleUpdate();
  }, [selectedIDs, notifications.length]);

  const handleMarkAsRead = () => {
    selectedIDs.forEach(async (selectedID) => {
      const response = await api.readNotification(selectedID);
      if (response.ok) {
        // Mark as read in state
        setNotifications((prevNotifications) => [
          ...prevNotifications.map((prevNotification) =>
            prevNotification.id === selectedID
              ? { ...prevNotification, read: true }
              : prevNotification,
          ),
        ]);
        // Remove read ID from selected ids
        setSelectedIDs((prevIDs) => prevIDs.filter((ID) => ID !== selectedID));
        // Sort notifications after mark as read // maybe better to do this in a use-effect observing a state?
        setNotifications((prevNotifications) =>
          sortNotifications(prevNotifications),
        );
        onNotificationRead();
      } else {
        dispatchToast({
          type: "add",
          variant: "warning",
          subheading: t("notifications:ui.toasts.error.subheading"),
        });
      }
    });

    // // Unselect all after mark all as read (bonus feature / can be disabled by commenting next setNotifications)
    // setNotifications((prevNotifications) => [
    //   ...prevNotifications.map((prevNotification) => ({
    //     ...prevNotification,
    //     selectedByControl: false,
    //   })),
    // ]);
  };
  return loading ? (
    <FormLoader />
  ) : (
    <div className="Notifications">
      {!companyID && (
        <NotificationsControls
          selectAllShown={selectAllShown}
          menuShown={menuShown}
          selectAllToggle={selectAllToggle}
          selectedIDs={selectedIDs}
          setSelectAllToggle={setSelectAllToggle}
          setMenuShown={setMenuShown}
          handleMarkAsRead={handleMarkAsRead}
        />
      )}
      <NotificationsList
        notifications={notifications}
        menuShown={menuShown}
        selectAllShown={selectAllShown}
      />
    </div>
  );
}

function NotificationsControls({
  selectAllShown,
  menuShown,
  selectAllToggle,
  selectedIDs,
  setSelectAllToggle,
  setMenuShown,
  handleMarkAsRead,
}) {
  const { t } = useTranslation(["notifications"]);

  return selectAllShown ? (
    <div className="Notifications-buttons-container">
      {menuShown && (
        <div className="Notifications-buttons">
          {selectAllShown && (
            <Input
              type="checkbox"
              label={
                selectedIDs.length > 0
                  ? `${selectedIDs.length}`
                  : t("notifications:ui.selectAll")
              }
              value={selectAllToggle}
              onChange={setSelectAllToggle}
            />
          )}
          {selectedIDs.length > 0 && (
            <Button
              onClick={handleMarkAsRead}
              text={
                selectedIDs.length === 1
                  ? t("notifications:ui.mark1AsRead")
                  : t("notifications:ui.markAsRead")
              }
              variant="secondary_b"
            />
          )}
        </div>
      )}

      {selectAllShown && (
        <Button
          onClick={() => setMenuShown((prev) => !prev)}
          icon={menuShown ? <Close /> : <CheckBoxOutlined />}
          className={"Notifications-buttons-activation"}
        />
      )}
    </div>
  ) : null;
}

function NotificationsList({ notifications, menuShown, selectAllShown }) {
  return (
    <div className="Notifications-list">
      {notifications.map((notification) => (
        <Fragment key={notification.id}>
          <Notification
            {...notification}
            selectAllShown={menuShown && selectAllShown}
            selectedByControl={
              notification.read ? false : notification.selectedByControl
            }
          />
        </Fragment>
      ))}
    </div>
  );
}
